import { CaptureRole } from '@ikegps/core-api';

export interface MidSpanRoleIndexes {
	/** Index of the measurable span shot, or null when it cannot be resolved. */
	spanIndex: number | null;
	/** Index of the ground reference shot, or null (mistagged pairs and single-capture migrated fields). */
	groundIndex: number | null;
}

/**
 * Resolve which entries of a MidSpan capture pair are the span shot and the ground reference shot.
 *
 * One definition shared by the model (`CollectionModelFieldMidSpan`, over resolved capture objects) and the
 * photo-measure summary route (over the role snapshots embedded in collection data), so the two can never
 * disagree about which photo is measurable.
 *
 * Rules, in order: a tagged role wins; if only the *other* role is tagged, whatever is left over must be
 * this one; the SPAN additionally falls back to the first capture, so the map dot and measurable photo
 * survive a pair whose Field build sent no roles. The positional fallback never assigns the span role to a
 * capture explicitly tagged as ground — a tag is authoritative, so a both-tagged-the-same pair resolves the
 * role nobody claimed to null rather than contradicting the tag. It is also suppressed entirely via
 * `allowPositionalFallback: false` while a client is still resolving captures, since position is
 * meaningless over a partial list.
 *
 * The GROUND role deliberately has no positional fallback: an untagged capture list is exactly what a
 * migrated legacy Mid Span IKE Photo looks like, and a positionally-invented ground would strip annotation
 * from an ordinary photo and falsely badge it as the reference shot.
 *
 * A null index here means "no capture claims this role", not "no photo to show": a MidSpan field owns all
 * of its captures, so visibility and exports never depend on this resolution — a mistagged pair merely gets
 * no map dot and no derived measurement.
 */
export function resolveMidSpanRoleIndexes(
	roles: ReadonlyArray<CaptureRole | undefined>,
	options: { allowPositionalFallback: boolean }
): MidSpanRoleIndexes {
	const spanIndex = resolveRoleIndex(roles, CaptureRole.MIDSPAN_SPAN, CaptureRole.MIDSPAN_GROUND, 0, options);
	let groundIndex = resolveRoleIndex(roles, CaptureRole.MIDSPAN_GROUND, CaptureRole.MIDSPAN_SPAN, null, options);

	if (groundIndex != null && groundIndex === spanIndex) {
		groundIndex = null;
	}

	return { spanIndex, groundIndex };
}

function resolveRoleIndex(
	roles: ReadonlyArray<CaptureRole | undefined>,
	role: CaptureRole,
	otherRole: CaptureRole,
	fallbackIndex: number | null,
	options: { allowPositionalFallback: boolean }
): number | null {
	const tagged = roles.findIndex((r) => r === role);
	if (tagged !== -1) {
		return tagged;
	}

	if (roles.some((r) => r === otherRole)) {
		const leftover = roles.findIndex((r) => r !== otherRole);
		if (leftover !== -1) {
			return leftover;
		}
	}

	if (
		fallbackIndex != null &&
		options.allowPositionalFallback &&
		fallbackIndex < roles.length &&
		roles[fallbackIndex] !== otherRole
	) {
		return fallbackIndex;
	}
	return null;
}
